import { DreamityPlanResDto } from '@/store/types/plan.types'
import { useLanguage } from '@/hooks/useLanguage'
import { useConfirmContext } from '@/store/contexts/ConfirmContext'
import { t } from '@/utils/i18n'
import { formatCurrency } from '@/utils/currency'
import { IconEdit, IconTrash } from '@/components/icons/Icons'
import './PlanCard.css'

interface PlanCardProps {
  plan: DreamityPlanResDto
  progressRate?: number // Item 완료율 기반 진행률 (0-1)
  onClick?: () => void
  onEdit?: (plan: DreamityPlanResDto) => void
  onDelete?: (planId: string) => void
  onToggleComplete?: (plan: DreamityPlanResDto) => void
}

function PlanCard({ plan, progressRate, onClick, onEdit, onDelete, onToggleComplete }: PlanCardProps) {
  const { language } = useLanguage()
  const { confirm } = useConfirmContext()

  // 진행률 계산: progressRate가 제공되면 사용, 없으면 isCompleted 기반
  const calculatedProgress = progressRate !== undefined 
    ? progressRate 
    : plan.isCompleted ? 1 : 0

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit?.(plan)
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const confirmed = await confirm({ 
      message: t('goalDetail.deletePlanConfirm', language),
      variant: 'danger'
    })
    if (confirmed) {
      onDelete?.(plan.planId)
    }
  }

  const handleToggleComplete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleComplete?.(plan)
  }

  return (
    <div className="plan-card" onClick={onClick}>
      <div className="plan-card__main">
        <div className="plan-card__header">
          <h4 className="plan-card__title">{plan.planName}</h4>
          <div className="plan-card__actions">
            {onEdit && (
              <button className="plan-card__action-btn" onClick={handleEdit} title={t('common.edit', language)}>
                <IconEdit size={16} />
              </button>
            )}
            {onDelete && (
              <button className="plan-card__action-btn plan-card__action-btn--danger" onClick={handleDelete} title={t('common.delete', language)}>
                <IconTrash size={16} />
              </button>
            )}
          </div>
        </div>

        {plan.planDesc && <p className="plan-card__desc">{plan.planDesc}</p>}

        <div className="plan-card__meta">
          <div className="plan-card__budget-badge">
            <span className="plan-card__budget-label">{t('dreams.itemBudget', language)}</span>
            <span className="plan-card__budget-value">{formatCurrency(plan.totalItemPrice || 0, language)}</span>
          </div>
          <span className={`plan-card__status-tag ${plan.isCompleted ? 'completed' : 'in-progress'}`}>
            {plan.isCompleted ? t('common.completed', language) : t('common.inProgress', language)}
          </span>
        </div>

        <div className="plan-card__footer">
          <div className="plan-card__date-info">
            <span className="plan-card__date">{plan.startDate.slice(5)} ~ {plan.endDate.slice(5)}</span>
            {plan.frequency && (
              <span className="plan-card__frequency">
                • {t(`plan.frequency.${plan.frequency.toLowerCase()}`, language)}
              </span>
            )}
          </div>
          <div className="plan-card__progress-container">
            <div className="plan-card__progress-bar-bg">
              <div className="plan-card__progress-bar-fill" style={{ width: `${calculatedProgress * 100}%` }} />
            </div>
            <span className="plan-card__progress-percent">{Math.round(calculatedProgress * 100)}%</span>
          </div>
        </div>
      </div>
      
      <button 
        className={`plan-card__toggle-btn ${plan.isCompleted ? 'completed' : ''}`}
        onClick={handleToggleComplete}
      >
        {plan.isCompleted ? t('common.completed', language) : t('common.inProgress', language)}
      </button>
    </div>
  )
}

export default PlanCard

